import { useContext, useCallback, useState, useEffect } from 'react';
import { PropertyEditorContext } from 'components/property-editors/contexts/PropertyEditorContext';
import { getEditorConfigValue } from 'components/property-editors/utils/EditorConfigurationUtils';
import { EditorLabel } from '../EditorLabel';
import { useEditorId } from '../hooks/useEditorId';
import { getEditorValidationSummary } from 'utils/ValidationUtils';
import {
  dataDescriptionFetcher,
  dataDescriptionTreeModel,
  getPickedEntityName,
  IDataDescriptionFetcher,
  submit,
} from './ReportFieldSelectorUtils';
import { IEntityPickerTreeModel } from 'components/EntityPicker/defaultEpickTreeModel';
import { IEpickNode } from 'components/EntityPicker/EpickTree';
import { IEditorProps } from '../Editor';
import { ComboBoxTree } from 'components/Tree/ComboBoxTree/ComboBoxTree';

interface IReportFieldSelectorEditorProps extends IEditorProps {}

/**
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export function ReportFieldSelectorEditor({ child, path }: IReportFieldSelectorEditorProps) {
  const [pickedEntityName, setPickedEntityName] = useState<string>(getPickedEntityName(child));
  const reportContextId = getEditorConfigValue(child, 'reportContextId');
  const isImageDataLoader = getEditorConfigValue(child, 'isImageDataLoader');
  const { editingContextId } = useContext(PropertyEditorContext);
  const validation = getEditorValidationSummary(child);
  const [id] = useEditorId(path);

  useEffect(() => {
    const pickedEntityName = getPickedEntityName(child);
    setPickedEntityName(pickedEntityName);
  }, [child]);

  const handleSelected = useCallback(
    async (item: IEpickNode | null) => {
      item && (await submit(editingContextId, path, item, isImageDataLoader));
    },
    [editingContextId, isImageDataLoader, path]
  );

  const handleCreateModel = useCallback(() => {
    const fetcher = dataDescriptionFetcher(reportContextId, isImageDataLoader) as IDataDescriptionFetcher;
    return dataDescriptionTreeModel(fetcher, isImageDataLoader) as IEntityPickerTreeModel;
  }, [isImageDataLoader, reportContextId]);

  return (
    <EditorLabel htmlFor={id} editor={child} path={path} className="dp-report-field-selector-editor">
      <ComboBoxTree
        searchValue={pickedEntityName}
        createModel={handleCreateModel}
        validation={validation}
        onSelected={handleSelected}
        handleSearch={false}
        hideClear
      />
    </EditorLabel>
  );
}
